import { createSlice } from '@reduxjs/toolkit';
import type {
  FeatureToggles,
  WhitelabelSlug,
  IUiVisibility,
  IPaymentMethod,
} from '@shore/types';
import {
  defaultUiVisibility,
  getUiVisibility,
} from '../utils/default-ui-visibility';
import { initializeSuccess, updateTrialPeriod } from '../actions';

export interface CurrentMerchantState {
  id: string | null;
  name: string;
  whiteLabel: WhitelabelSlug | null;
  createdAt: string | null;
  isSelfSignUp: boolean;
  subscriptionStatus: string | undefined;
  subscriptionPartner: string | undefined;
  subscriptionPackageCode: string | undefined;
  subscriptionActivationAllowed: boolean;
  trialEndsOn: string | null;
  trialDaysLeft: number | null;
  featureToggles: FeatureToggles;
  source: string;
  uiVisibility: IUiVisibility;
  category: string;
  currency: string;
  isTest: boolean;
  packageCode: string | null;
  pixelId: string | null;
  phoneNumber: string | null;
  sessionRole: string | null;
  paymentSettings: {
    paymentMethods: IPaymentMethod[];
  } | null;
  timeZone: string | null;
}

const initialState: CurrentMerchantState = {
  id: null,
  name: '',
  whiteLabel: null,
  createdAt: null,
  isSelfSignUp: false,
  subscriptionStatus: undefined,
  subscriptionPartner: undefined,
  subscriptionPackageCode: undefined,
  subscriptionActivationAllowed: false,
  trialEndsOn: null,
  trialDaysLeft: null,
  featureToggles: {},
  source: '',
  uiVisibility: defaultUiVisibility,
  category: '',
  currency: 'EUR',
  isTest: false,
  packageCode: null,
  pixelId: null,
  phoneNumber: null,
  sessionRole: null,
  paymentSettings: null,
  timeZone: null,
};

const merchantSlice = createSlice({
  name: 'currentMerchant',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(initializeSuccess, (state, action) => {
        const {
          currentMerchantData,
          packageCode,
          sessionRole,
          subscription,
          paymentSettings,
        } = action.payload;

        const org = currentMerchantData.organization;

        return {
          ...state,
          id: currentMerchantData.id,
          name: currentMerchantData.name,
          createdAt: currentMerchantData.created_at,
          whiteLabel: org?.whitelabel_slug ?? null,
          isSelfSignUp: org?.self_sign_up ?? false,
          subscriptionStatus: subscription?.status || undefined,
          subscriptionPackageCode: subscription?.plan?.id || undefined,
          subscriptionPartner: subscription?.customer?.partner || undefined,
          subscriptionActivationAllowed:
            subscription?.activation_allowed ?? false,
          trialEndsOn: subscription?.trial_end ?? org?.trial_ends_on ?? null,

          trialDaysLeft: subscription
            ? (subscription.trial_days_left ?? null)
            : (org?.trial_days_left ?? null),
          isTest: org?.test ?? false,
          packageCode: packageCode ?? org?.package_code ?? null,

          featureToggles: currentMerchantData.feature_toggles ?? {},
          source: currentMerchantData.source,
          category: currentMerchantData.category,
          currency: currentMerchantData.currency,
          uiVisibility: getUiVisibility(currentMerchantData),
          pixelId: currentMerchantData.pixel_id,
          phoneNumber: currentMerchantData.phone || '',
          timeZone: currentMerchantData.time_zone ?? null,
          sessionRole,
          paymentSettings: paymentSettings
            ? { paymentMethods: paymentSettings.payment_methods || [] }
            : null,
        };
      })
      .addCase(updateTrialPeriod, (state, action) => {
        state.trialEndsOn = action.payload;
      });
  },
});

export const currentMerchantReducer = merchantSlice.reducer;
